import React, { useState, useEffect } from 'react';
import { Button, Alert } from '../ui';
import { db } from '../../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useSalon } from '../../contexts/SalonContext';
import { formatWhatsApp } from '../../utils/formatUtils';

const MobileClientForm = ({
  onSubmit,
  onBack,
  loading,
  error,
  initialData = {},
  selectedServices = [],
  selectedDate,
  selectedTime,
  selectedProfessional,
  services = [],
  professionals = []
}) => {
  const { salonId } = useSalon();
  const [professionalCustomPrices, setProfessionalCustomPrices] = useState({});
  
  const [formData, setFormData] = useState({
    cliente: initialData.cliente || '',
    whatsapp: initialData.whatsapp || '',
    email: initialData.email || '',
    confirmado: initialData.confirmado || isSameDay(selectedDate),
    pagamentoConfirmado: initialData.pagamentoConfirmado || false
  });
  
  const [formErrors, setFormErrors] = useState({
    cliente: '',
    whatsapp: '',
    email: ''
  });
  
  // Verificar se a data selecionada é hoje
  function isSameDay(dateString) {
    if (!dateString) return false;
    
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    
    const todayFormatted = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    
    return dateString === todayFormatted;
  }
  
  // Buscar preços personalizados quando o profissional for selecionado
  useEffect(() => {
    const fetchCustomPrices = async () => {
      if (!salonId || !selectedProfessional || !selectedServices.length) return;
      
      try {
        const customPrices = {};
        
        // Consultar preços personalizados para este profissional e serviços selecionados
        const professionalServicesRef = collection(db, `salons/${salonId}/professional_services`);
        const q = query(
          professionalServicesRef,
          where('professionalId', '==', selectedProfessional),
          where('isActive', '==', true)
        );
        
        const snapshot = await getDocs(q);
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (selectedServices.includes(data.serviceId) && data.customPrice !== null) {
            customPrices[data.serviceId] = {
              price: data.customPrice,
              serviceName: data.serviceName
            };
          }
        });
        
        setProfessionalCustomPrices(customPrices);
      } catch (error) {
        console.error("Erro ao buscar preços personalizados:", error);
      }
    };
    
    fetchCustomPrices();
  }, [salonId, selectedProfessional, selectedServices]);
  
  // Variáveis para mostrar o resumo
  const servicosDetalhes = selectedServices.map(id => {
    const service = services.find(s => s.id === id);
    if (!service) return null;
    
    // Verificar se tem preço personalizado
    const customPrice = professionalCustomPrices[id];
    
    return {
      ...service,
      preco: customPrice ? customPrice.price : service.preco
    };
  }).filter(Boolean);
  
  const professional = professionals.find(p => p.id === selectedProfessional);
  
  // Calcular valores totais
  const totalPrice = servicosDetalhes.reduce((sum, s) => sum + (s.preco || 0), 0);
  const totalDuration = servicosDetalhes.reduce((sum, s) => sum + (s.duracao || 0), 0);
  
  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };
  
  // Formatação de preço
  const formatPrice = (price) => {
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };
  
  // Formatação de duração
  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };
  
  // Validar telefone/whatsapp
  const isValidPhone = (phone) => {
    // Formato brasileiro: (XX) XXXXX-XXXX ou XXXXXXXXXXX
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 11;
  };
  
  // Validar email
  const isValidEmail = (email) => {
    if (!email) return true; // Email é opcional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Manipulador de mudança nos campos do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Formatar WhatsApp em tempo real
    if (name === 'whatsapp') {
      const formatted = formatWhatsApp(value);
      setFormData(prev => ({
        ...prev,
        whatsapp: formatted
      }));
    } else {
      // Atualizar outros campos normalmente
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Limpar erro do campo
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Manipulador para toggles (campos de checkbox booleanos)
  const handleToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  // Formatar número de whatsapp ao perder o foco
  const handleWhatsappBlur = (e) => {
    const value = e.target.value;
    if (!value) return;
    
    // Usar a função formatWhatsApp centralizada
    const formatted = formatWhatsApp(value);
    
    setFormData(prev => ({
      ...prev,
      whatsapp: formatted
    }));
  };
  
  // Validar formulário antes de enviar
  const validateForm = () => {
    const errors = {
      cliente: !formData.cliente ? 'Nome é obrigatório' : '',
      whatsapp: !formData.whatsapp ? 'WhatsApp é obrigatório' : 
                !isValidPhone(formData.whatsapp) ? 'Número de WhatsApp inválido' : '',
      email: !isValidEmail(formData.email) ? 'E-mail inválido' : ''
    };
    
    setFormErrors(errors);
    
    // Verificar se há erros
    return !Object.values(errors).some(error => error);
  };
  
  // Manipulador de envio do formulário
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Formatar whatsapp para armazenamento (apenas números)
      const whatsapp = formData.whatsapp.replace(/\D/g, '');
      
      onSubmit({
        ...formData,
        whatsapp,
        data: selectedDate,
        dataFormatada: formatDate(selectedDate),
        horario: selectedTime,
        profissional: selectedProfessional,
        servicos: selectedServices,
        servicosDetalhes: servicosDetalhes,
        duracaoTotal: totalDuration,
        valorTotal: totalPrice,
        professionalCustomPrices: professionalCustomPrices
      });
    }
  };

  // Estilos inline para os toggles
  const toggleStyles = `
    .toggle-checkbox:checked {
      right: 0;
      border-color: #68D391;
    }
    .toggle-checkbox:checked + .toggle-label {
      background-color: #68D391;
    }
    .toggle-label {
      transition: background-color 0.2s ease;
    }
  `;

  return (
    <div className="space-y-6">
      <style>{toggleStyles}</style>
      <h2 className="text-xl font-semibold text-gray-800 mb-2">
        Seus dados de contato
      </h2>
      
      {/* Resumo do agendamento */}
      <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
        <h3 className="font-medium text-blue-800 mb-3">Resumo do agendamento</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-700">Data:</span>
            <span className="font-medium">{formatDate(selectedDate)} às {selectedTime}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-700">Profissional:</span>
            <span className="font-medium">{professional?.nome || 'Não selecionado'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-700">Serviços:</span>
            <span className="font-medium">{servicosDetalhes.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-700">Duração:</span>
            <span className="font-medium">{formatDuration(totalDuration)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-700">Valor total:</span>
            <span className="font-medium text-blue-700">{formatPrice(totalPrice)}</span>
          </div>
        </div>
      </div>
      
      {error && (
        <Alert type="error" className="mb-4">
          {error}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Campo de nome */}
          <div>
            <label htmlFor="cliente" className="block text-sm font-medium text-gray-700 mb-1">
              Nome completo
            </label>
            <input
              id="cliente"
              name="cliente"
              type="text"
              value={formData.cliente}
              onChange={handleChange}
              className={`w-full px-3 py-3 rounded-lg border ${
                formErrors.cliente ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Digite seu nome completo"
            />
            {formErrors.cliente && (
              <p className="mt-1 text-sm text-red-600">{formErrors.cliente}</p>
            )}
          </div>
          
          {/* Campo de WhatsApp */}
          <div>
            <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp
            </label>
            <input
              id="whatsapp"
              name="whatsapp"
              type="tel"
              value={formData.whatsapp}
              onChange={handleChange}
              onBlur={handleWhatsappBlur}
              className={`w-full px-3 py-3 rounded-lg border ${
                formErrors.whatsapp ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="(00) 00000-0000"
            />
            {formErrors.whatsapp ? (
              <p className="mt-1 text-sm text-red-600">{formErrors.whatsapp}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Você receberá a confirmação via WhatsApp
              </p>
            )}
          </div>
          
          {/* Campo de Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              E-mail (opcional)
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-3 rounded-lg border ${
                formErrors.email ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="seu@email.com"
            />
            {formErrors.email && (
              <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
            )}
          </div>
          
          {/* Status do Agendamento */}
          <div className="mt-6 space-y-4">
            <h3 className="text-sm font-medium text-gray-700">Status do agendamento</h3>
            
            <div className="p-3 rounded-lg border border-gray-200 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-800">Confirmar agendamento</p>
                <p className="text-xs text-gray-500">Agendamento aparecerá na agenda como confirmado</p>
              </div>
              <div className="relative inline-block w-10 align-middle select-none">
                <input 
                  id="confirmado"
                  type="checkbox"
                  checked={formData.confirmado}
                  onChange={() => handleToggle('confirmado')}
                  className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                />
                <label
                  htmlFor="confirmado"
                  className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                ></label>
              </div>
            </div>
            
            <div className="p-3 rounded-lg border border-gray-200 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-800">Pagamento confirmado</p>
                <p className="text-xs text-gray-500">Cliente já realizou o pagamento</p>
              </div>
              <div className="relative inline-block w-10 align-middle select-none">
                <input 
                  id="pagamentoConfirmado"
                  type="checkbox"
                  checked={formData.pagamentoConfirmado}
                  onChange={() => handleToggle('pagamentoConfirmado')}
                  className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                />
                <label
                  htmlFor="pagamentoConfirmado"
                  className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                ></label>
              </div>
            </div>
          </div>
        </div>
        
        {/* Barra inferior fixa com botões */}
        <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg border-t border-gray-200 z-10 flex justify-between">
          <Button 
            type="button"
            onClick={onBack} 
            variant="outline"
          >
            Voltar
          </Button>
          
          <Button 
            type="submit"
            variant="primary"
            loading={loading}
          >
            Confirmar Agendamento
          </Button>
        </div>
        
        {/* Espaço para evitar que o conteúdo fique atrás da barra fixa */}
        <div className="h-16"></div>
      </form>
    </div>
  );
};

export default MobileClientForm; 